import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, createElementVNode as _createElementVNode, withModifiers as _withModifiers, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-76db2778"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "hotspot-page__content" }
const _hoisted_2 = { class: "hotspot-page__body" }
const _hoisted_3 = { class: "hotspot-page__panel" }
const _hoisted_4 = {
  key: 0,
  class: "hotspot-page__state"
}
const _hoisted_5 = {
  key: 1,
  class: "hotspot-page__state"
}
const _hoisted_6 = { class: "hotspot-page__open-app" }

import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import HotspotApi from "@/apis/hotspot";
import DownloadBanner from "@/components/DownloadBanner";
import MainButton from "@/components/MainButton";
import { useTitle } from "@/hooks/useTitle";
import { invokeAppByYYB } from "@/utils/mob";
import ActivityCalendar from "./components/ActivityCalendar.vue";
import AuctionFieldSection from "./components/AuctionFieldSection.vue";
import Feeds from "./components/Feeds.vue";
import GrouponCarousel from "./components/GrouponCarousel.vue";
import HotspotHeader from "./components/Header.vue";
import HotspotHero from "./components/Hero.vue";
import ShelfCarousel from "./components/ShelfCarousel.vue";
import {
  selectHotspotAuctionFieldSection,
  selectHotspotGrouponSection,
  selectHotspotJumpLinkSection,
  selectHotspotShelfSection,
} from "./model";
import type {
  HotspotFeed,
  HotspotFeedListResponse,
  HotspotSectionResponse,
  HotspotSimpleInfo,
} from "./model";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const route = useRoute();
const { setTitle } = useTitle();

const loading = ref(false);
const hotspotInfo = ref<HotspotSimpleInfo | null>(null);
const feeds = ref<HotspotFeed[]>([]);
const sectionResponse = ref<HotspotSectionResponse | null>(null);
let requestSerial = 0;

const hotspotId = computed(() => String(route.query.id || ""));

const jumpLinkSection = computed(() =>
  selectHotspotJumpLinkSection(sectionResponse.value)
);
const grouponSection = computed(() =>
  selectHotspotGrouponSection(sectionResponse.value)
);
const shelfSection = computed(() =>
  selectHotspotShelfSection(sectionResponse.value)
);

// 拍场状态（进行中/预展/已结束）和倒计时依赖当前时间，每 30 秒刷新一次
const now = ref(Date.now());
const nowTimer = window.setInterval(() => {
  now.value = Date.now();
}, 30000);

const auctionFields = computed(() =>
  selectHotspotAuctionFieldSection(sectionResponse.value, now.value)
);

// 仅拍场 section：其他 section 和帖子都没有数据时，拍场改为纵向大卡片
const isAuctionFieldOnly = computed(() => {
  return Boolean(
    auctionFields.value &&
      !jumpLinkSection.value?.list?.length &&
      !grouponSection.value?.list?.length &&
      !shelfSection.value?.list?.length &&
      !feeds.value.length
  );
});

const hasContent = computed(() => {
  return Boolean(
    hotspotInfo.value?.name ||
      jumpLinkSection.value?.list?.length ||
      auctionFields.value?.list?.length ||
      grouponSection.value?.list?.length ||
      shelfSection.value?.list?.length ||
      feeds.value.length
  );
});

const syncTitle = () => {
  const title = hotspotInfo.value?.name
    ? `${hotspotInfo.value.name} - 千岛热点`
    : "千岛热点";
  setTitle(title);
};

const openApp = () => {
  if (!hotspotId.value) return;
  invokeAppByYYB({
    targetPath: "hotspot",
    params: {
      id: hotspotId.value,
    },
  });
};

const resetData = () => {
  hotspotInfo.value = null;
  feeds.value = [];
  sectionResponse.value = null;
};

const fetchData = async (currentId: string) => {
  if (!currentId) {
    resetData();
    loading.value = false;
    syncTitle();
    return;
  }

  requestSerial += 1;
  const currentRequestSerial = requestSerial;
  resetData();
  loading.value = true;

  const [simpleInfoRes, feedsRes, sectionInfoRes] = await Promise.allSettled([
    HotspotApi.getSimpleInfo(currentId),
    HotspotApi.getFeeds({
      andTagIds: [currentId],
      entryType: "hotspot",
      mediaAttachment: true,
      orderBy: "id",
      style: "doubleColumns",
      limit: 20,
      offset: 0,
    }),
    HotspotApi.getSectionInfo(currentId),
  ]);

  if (currentRequestSerial !== requestSerial || currentId !== hotspotId.value) {
    return;
  }

  hotspotInfo.value =
    simpleInfoRes.status === "fulfilled"
      ? simpleInfoRes.value?.data || null
      : null;
  feeds.value =
    feedsRes.status === "fulfilled"
      ? (feedsRes.value?.data as HotspotFeedListResponse)?.posts || []
      : [];
  sectionResponse.value =
    sectionInfoRes.status === "fulfilled"
      ? (sectionInfoRes.value?.data as HotspotSectionResponse)
      : null;

  loading.value = false;
  syncTitle();
};

watch(
  () => route.query.id,
  (id) => {
    const currentId = String(id || "");
    if (!currentId) {
      requestSerial += 1;
    }
    fetchData(currentId);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  requestSerial += 1;
  window.clearInterval(nowTimer);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "hotspot-page",
    onGesturestart: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["prevent"])),
    onGesturechange: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"]))
  }, [
    _createVNode(_unref(DownloadBanner), { onOnInvokeApp: openApp }),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(HotspotHero, {
        image: hotspotInfo.value?.image?.cover,
        class: "hotspot-page__hero"
      }, null, 8, ["image"]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(HotspotHeader, {
          title: hotspotInfo.value?.name || '千岛热点',
          description: hotspotInfo.value?.description || ''
        }, null, 8, ["title", "description"]),
        _createElementVNode("div", _hoisted_3, [
          (loading.value && !_unref(hasContent))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, " 加载中... "))
            : (!loading.value && !_unref(hasContent))
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, " 暂无热点内容 "))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                  _createVNode(ActivityCalendar, {
                    items: _unref(jumpLinkSection)?.list || [],
                    onOpenApp: openApp
                  }, null, 8, ["items"]),
                  (_unref(auctionFields))
                    ? (_openBlock(), _createBlock(AuctionFieldSection, {
                        key: 0,
                        section: _unref(auctionFields),
                        layout: _unref(isAuctionFieldOnly) ? 'list' : 'carousel',
                        now: now.value,
                        onOpenApp: openApp
                      }, null, 8, ["section", "layout", "now"]))
                    : _createCommentVNode("", true),
                  (_unref(grouponSection))
                    ? (_openBlock(), _createBlock(GrouponCarousel, {
                        key: 1,
                        section: _unref(grouponSection),
                        onOpenApp: openApp
                      }, null, 8, ["section"]))
                    : _createCommentVNode("", true),
                  (_unref(shelfSection))
                    ? (_openBlock(), _createBlock(ShelfCarousel, {
                        key: 2,
                        section: _unref(shelfSection),
                        onOpenApp: openApp
                      }, null, 8, ["section"]))
                    : _createCommentVNode("", true),
                  _createVNode(Feeds, {
                    feeds: feeds.value,
                    onOpenApp: openApp
                  }, null, 8, ["feeds"])
                ], 64))
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createVNode(_unref(MainButton), {
        size: "large",
        text: "临界App内打开",
        onOnInvokeApp: openApp
      })
    ])
  ], 32))
}
}

})